// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import XngZone from "@awc/App/Components/Common/XngZone";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {
    extendedStyles?: FooterContainerStyles;
}

export interface FooterContainerStyles {
    widgetGridContainer?: GridContainerProps;
    logoGridItem?: GridItemProps;
    linkList1GridItem?: GridItemProps;
    linkList2GridItem?: GridItemProps;
    linkList3GridItem?: GridItemProps;
    linkList4GridItem?: GridItemProps;
    subscribeGridItem?: GridItemProps;
    socialLinksGridItem?: GridItemProps;
    siteMapLinksGridItem?: GridItemProps;
    richContentGridItem?: GridItemProps;
    logoWrapper?: InjectableCss;
    // XNG-Change
    container?: InjectableCss;
    innerContainer?: InjectableCss;
    bottomSectionContainer?: InjectableCss;
    linkListSectionContainer?: InjectableCss;
    linkListWrapper?: InjectableCss;
    rightSection?: InjectableCss;
    leftSection?: InjectableCss;
    copyrightText?: TypographyPresentationProps;
}

export const footerContainerStyles: FooterContainerStyles = {
    logoGridItem: {
        width: 12,
        css: css`
            width: 100%;
            display: flex;
            justify-content: center;
            img {
                height: 78px;
            }
        `,
    },
    linkList1GridItem: {
        width: [6, 6, 4, 2, 2],
    },
    linkList2GridItem: {
        width: [6, 6, 4, 2, 2],
    },
    linkList3GridItem: {
        width: [6, 6, 4, 2, 2],
    },
    linkList4GridItem: {
        width: [6, 6, 4, 2, 2],
    },
    subscribeGridItem: {
        width: [12, 12, 8, 4, 4],
    },
    socialLinksGridItem: {
        width: 12,
    },
    siteMapLinksGridItem: {
        width: 12,
    },
    richContentGridItem: {
        width: 12,
    },
    logoWrapper: {
        css: css`
            width: 110px;
        `,
    },
};

// XNG-Changes: structure/layout changes
const FooterContainer: FC<OwnProps> = ({ id, extendedStyles }) => {
    const [styles] = React.useState(() => mergeToNew(footerContainerStyles, extendedStyles));
    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.innerContainer}>
                <StyledWrapper {...styles.leftSection}>
                    <XngZone zoneName="Logo" contentId={id} hint="Logo" />
                    <XngZone zoneName="SocialLinks" contentId={id} hint="Social Links" />
                    <StyledWrapper className="privacy-choices-wrapper divider">
                        <XngZone zoneName="PrivacyChoices" contentId={id} hint="Privacy Choices" />
                    </StyledWrapper>
                    <StyledWrapper className="contact-us-wrapper divider">
                        <XngZone zoneName="ContactUs" contentId={id} hint="Contact Us" />
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...styles.rightSection}>
                    <StyledWrapper {...styles.linkListSectionContainer}>
                        <StyledWrapper {...styles.linkListWrapper}>
                            <XngZone zoneName="LinkList1" contentId={id} hint="Link List1" />
                        </StyledWrapper>
                        <StyledWrapper {...styles.linkListWrapper}>
                            <XngZone zoneName="LinkList2" contentId={id} hint="Link List2" />
                        </StyledWrapper>
                        <StyledWrapper {...styles.linkListWrapper}>
                            <XngZone zoneName="LinkList3" contentId={id} hint="Link List3" />
                        </StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper {...styles.bottomSectionContainer}>
                        <XngZone zoneName="Subscribe" contentId={id} hint="Subscribe" />
                        <Typography {...styles.copyrightText}>
                            {siteMessage(`Custom_Footer_Copyright`, String(new Date().getFullYear()))}
                        </Typography>
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const footerContainer: WidgetModule = {
    component: FooterContainer,
    definition: {
        group: "Footer",
        allowedContexts: ["Footer"],
    },
};

export default footerContainer;
